import type { RecordableMeeting } from '@/hooks/useTodaysMeetings'
import type { RecordingEventContext } from '@/lib/utils/tauri-recording'
import { useEffect, useRef } from 'react'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useTodaysMeetings } from '@/hooks/useTodaysMeetings'
import { meetingRecordingsApi } from '@/lib/api/meeting-recordings.api'
import { meetingSummaryTemplatesApi } from '@/lib/api/meeting-summary-templates.api'
import { createLogger } from '@/lib/logger'
import { isTauri } from '@/lib/utils/tauri'
import {
  detectActiveMeeting,
  resolveLiveCaptionsEnabled,
  startRecording,
  stopRecording,
  updateRecordingLink,
} from '@/lib/utils/tauri-recording'
import {
  announceRecordingStarted,
  preflightRecordingStart,
  reportPreflightFailure,
  useMeetingRecorderAvailability,
} from '@/modules/meeting-recorder'
import { browserNotificationService } from '@/services/browserNotificationService'
import { useOrganizationStore } from '@/store/organization.store'
import { useRecordingStore } from '@/store/recording.store'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

const logger = createLogger('meeting-reminder-controller')

// How close to the meeting boundary we fire each prompt.
const START_LEAD_MS = 60 * 1000 // notify ~1 min before start
const START_GRACE_MS = 5 * 60 * 1000 // …and up to 5 min after, in case we missed it
const END_LEAD_MS = 60 * 1000 // "wrap up?" ~1 min before scheduled end
const END_GRACE_MS = 5 * 60 * 1000
// How close a manual recording's start must be to a meeting's start for us to
// consider it "started in alignment with" that meeting (and thus eligible for
// the end-of-meeting stop prompt).
const START_ALIGN_TOLERANCE_MS = 5 * 60 * 1000
const TICK_MS = 60 * 1000
// Ad-hoc detection (conferencing app + mic in use, no calendar entry)
const DETECT_TICK_MS = 30 * 1000
// After the user ignores/dismisses an ad-hoc prompt, stay quiet for this long
const DETECT_SNOOZE_MS = 60 * 60 * 1000

// ---- smart end detection (content + audio signals, beyond the calendar) ----
const SMART_END_TICK_MS = 5 * 1000
// VU levels (0–1 RMS) below this on BOTH mic and system count as silence
const SMART_END_SILENCE_LEVEL = 0.015
// Farewell words + this much silence afterwards → "meeting wrapped up?"
const FAREWELL_CONFIRM_SILENCE_MS = 45 * 1000
// A farewell not followed by silence within this window was a false alarm
const FAREWELL_EXPIRY_MS = 3 * 60 * 1000
// No farewell needed: this much continuous silence → "still recording?"
const LONG_SILENCE_MS = 4 * 60 * 1000
// Never prompt in the first minutes of a recording
const SMART_END_MIN_RECORDING_MS = 5 * 60 * 1000

// High-precision closing phrases (matched case-insensitively against live
// caption lines). Deliberately conservative — a false "stop?" prompt during a
// live meeting is worse than a missed one; long silence is the safety net.
const FAREWELL_PHRASES = [
  'thanks everyone',
  'thank you everyone',
  'thanks all',
  'thank you all',
  'thanks everybody',
  'bye everyone',
  'bye everybody',
  'goodbye everyone',
  'see you all',
  'see you next',
  'see you tomorrow',
  'talk to you next',
  'talk soon',
  'have a good one',
  'have a great rest of',
  'have a good rest of',
  'take care everyone',
  "that's a wrap",
  "let's wrap up",
  'we can wrap up',
  'wrap it up there',
  'end the meeting',
  'stop the recording',
  'catch you all later',
]

function containsFarewell(text: string): boolean {
  const lower = text.toLowerCase()
  return FAREWELL_PHRASES.some(phrase => lower.includes(phrase))
}

function dayKey(): string {
  return new Date().toISOString().slice(0, 10)
}

/**
 * Stable key for a meeting series: Google recurring-event instance ids are
 * `<seriesId>_<instanceSuffix>`, so the prefix groups all occurrences — the
 * template default chosen once applies to every future occurrence.
 */
function seriesKey(externalEventId: string): string {
  return externalEventId.split('_')[0] || externalEventId
}

/** Per-day persisted dedup set, so a reload doesn't re-fire today's prompts. */
function loadDayset(prefix: string): Set<string> {
  try {
    const raw = localStorage.getItem(`${prefix}:${dayKey()}`)
    return new Set(raw ? (JSON.parse(raw) as string[]) : [])
  } catch {
    return new Set()
  }
}

function persistDayset(prefix: string, set: Set<string>): void {
  try {
    localStorage.setItem(`${prefix}:${dayKey()}`, JSON.stringify([...set]))
  } catch {
    /* localStorage unavailable — in-memory dedup still holds for the session */
  }
}

/**
 * Headless desktop controller that nudges the user to record video meetings.
 * Mounted once in MainLayout (desktop only). It fires a LOCAL OS notification
 * (never written to the in-app notifications popover) as a meeting starts, and
 * a "stop recording?" prompt around the scheduled end while a recording is
 * running. Clicking either acts immediately; an in-app toast mirrors the prompt
 * when the window is focused, since desktop notification-click callbacks are
 * unreliable.
 */
export function MeetingReminderController() {
  const recordingState = useRecordingStore(s => s.state)
  const smartEndPromptedRef = useRef<string | null>(null)
  const smartEndScanRef = useRef<{ recordingId: string | null; count: number }>(
    { recordingId: null, count: 0 },
  )
  const { meetings, enabled } = useTodaysMeetings()
  const { checkFeatureFlag } = useFeatureFlags()
  const queryClient = useQueryClient()
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  // Never nudge users whose plan can't record (Free/Core) or personal spaces
  const { availability } = useMeetingRecorderAvailability()
  const isRecorderAvailable = availability === 'available'
  const recorderAvailableRef = useRef(isRecorderAvailable)
  recorderAvailableRef.current = isRecorderAvailable
  // Ad-hoc detection only needs desktop + flag + plan — no calendar connection.
  const detectEnabled =
    isTauri() && checkFeatureFlag('MEETING_NOTES') && isRecorderAvailable

  // Latest meetings for the interval closure without re-creating the timer.
  const meetingsRef = useRef<RecordableMeeting[]>([])
  meetingsRef.current = meetings

  // Per-meeting-series summary template defaults (set via RecordButton).
  const { data: templatePrefs } = useQuery({
    queryKey: ['meeting-template-preferences'],
    queryFn: () => meetingSummaryTemplatesApi.listPreferences(),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
  const templatePrefsRef = useRef<{ eventKey: string; templateRef: string }[]>(
    [],
  )
  templatePrefsRef.current = templatePrefs ?? []

  // Dedup sets (persisted per day).
  const notifiedStartRef = useRef<Set<string>>(
    loadDayset('meeting-reminder-notified'),
  )
  const notifiedEndRef = useRef<Set<string>>(
    loadDayset('meeting-reminder-ended'),
  )
  // The meeting we offered/started, so the end prompt can target it even though
  // the recording itself isn't linked to a calendar event id.
  const activeMeetingIdRef = useRef<string | null>(null)
  // Ad-hoc detection: don't re-prompt while the same call session continues,
  // and snooze after a prompt regardless of the user's choice.
  const detectSessionActiveRef = useRef(false)
  const detectSnoozedUntilRef = useRef(0)

  // ---- actions -------------------------------------------------------------
  const beginRecording = async (
    title: string,
    meetingId?: string,
    eventContext?: RecordingEventContext,
    options?: { summaryTemplate?: string; eventKey?: string },
  ) => {
    try {
      // Same plan + consent pre-flight as the record button, BEFORE capture
      const preflight = await preflightRecordingStart({
        queryClient,
        organizationId,
        surface: 'desktop',
      })
      if (!preflight.ok) {
        reportPreflightFailure(preflight)
        return
      }
      // Honor the remembered Live Captions preference for auto-started
      // recordings too (calendar prompts + ad-hoc detection), not just the
      // record button — same checkbox, same behavior everywhere.
      const liveCaptions = await resolveLiveCaptionsEnabled()
      await startRecording({
        ...(eventContext ? { eventContext } : {}),
        ...(options?.summaryTemplate
          ? { summaryTemplate: options.summaryTemplate }
          : {}),
        liveCaptions,
      })
      if (meetingId) activeMeetingIdRef.current = meetingId
      // Mirror into the store so RecordButton shows the applied template and
      // saves changes back as this series' default.
      const store = useRecordingStore.getState()
      store.setSummaryTemplate(options?.summaryTemplate ?? null)
      store.setActiveEventKey(options?.eventKey ?? null)
      announceRecordingStarted({
        title: `Recording "${title}"`,
        isOutOfCredits: preflight.isOutOfCredits,
      })
    } catch (error) {
      logger.error('Failed to start recording from reminder', error)
      toast.error("Couldn't start recording", {
        description:
          'Use the Record button in the title bar — you may need to grant microphone or system-audio access first.',
      })
    }
  }

  const beginRecordingForMeeting = async (meeting: RecordableMeeting) => {
    const eventKey = seriesKey(meeting.id)
    const preference = templatePrefsRef.current.find(
      p => p.eventKey === eventKey,
    )
    await beginRecording(
      meeting.title,
      meeting.id,
      {
        externalEventId: meeting.id,
        title: meeting.title,
        provider: meeting.conferenceProvider,
        attendees: meeting.attendees,
      },
      { summaryTemplate: preference?.templateRef, eventKey },
    )
    void suggestModuleLink(meeting)
  }

  // Auto-link the meeting to the matching record (attendee email domains →
  // entity). Only fires on an unambiguous single match, after the recording
  // is running and only while the user hasn't picked a link themselves.
  const suggestModuleLink = async (meeting: RecordableMeeting) => {
    const emails = meeting.attendees
      .map(a => a.email)
      .filter((email): email is string => Boolean(email))
    if (!emails.length) return
    try {
      const { suggestion } = await meetingRecordingsApi.suggestLink(emails)
      if (!suggestion) return
      const store = useRecordingStore.getState()
      if (store.state === 'idle' || store.linkedModule) return
      store.setLinkedModule({
        moduleType: suggestion.moduleType,
        moduleId: suggestion.moduleId,
        moduleName: suggestion.name,
      })
      await updateRecordingLink(suggestion.moduleType, suggestion.moduleId)
      toast(`Linked to ${suggestion.name}`, {
        description:
          'Matched from the attendee list — change it in the record button if that’s wrong.',
      })
    } catch (error) {
      logger.error('Module link suggestion failed', error)
    }
  }

  const endRecording = async () => {
    try {
      await stopRecording()
      activeMeetingIdRef.current = null
      toast('Recording stopped — generating notes…')
    } catch (error) {
      logger.error('Failed to stop recording from reminder', error)
      toast.error("Couldn't stop the recording")
    }
  }

  // ---- notification click bridge (from browserNotificationService) ---------
  useEffect(() => {
    if (!isTauri() || !enabled) return

    const onStart = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        title?: string
        eventId?: string
      }
      const meeting = detail?.eventId
        ? meetingsRef.current.find(m => m.id === detail.eventId)
        : undefined
      if (meeting) {
        void beginRecordingForMeeting(meeting)
      } else {
        void beginRecording(detail?.title ?? 'Meeting', detail?.eventId)
      }
    }
    const onStop = () => void endRecording()

    window.addEventListener('scrambl:meeting-record', onStart)
    window.addEventListener('scrambl:meeting-record-stop', onStop)
    return () => {
      window.removeEventListener('scrambl:meeting-record', onStart)
      window.removeEventListener('scrambl:meeting-record-stop', onStop)
    }
  }, [enabled])

  // ---- periodic boundary check ---------------------------------------------
  useEffect(() => {
    if (!isTauri() || !enabled) return

    const tick = () => {
      const now = Date.now()
      const { state, startedAt } = useRecordingStore.getState()
      const isRecording = state !== 'idle'
      const recordingStartMs = startedAt ? new Date(startedAt).getTime() : null

      for (const meeting of meetingsRef.current) {
        const start = meeting.start.getTime()

        // --- meeting starting: prompt to record (only when idle) ---
        if (
          !isRecording &&
          recorderAvailableRef.current &&
          !notifiedStartRef.current.has(meeting.id) &&
          now >= start - START_LEAD_MS &&
          now <= start + START_GRACE_MS
        ) {
          notifiedStartRef.current.add(meeting.id)
          persistDayset('meeting-reminder-notified', notifiedStartRef.current)
          fireStartPrompt(meeting, () => void beginRecordingForMeeting(meeting))
        }
      }

      // --- meeting ending: prompt to stop (only while recording, and only if
      // this recording was started in alignment with a meeting) ---
      if (isRecording) {
        const target = pickEndCandidate(
          meetingsRef.current,
          activeMeetingIdRef.current,
          recordingStartMs,
        )
        if (
          target?.end &&
          !notifiedEndRef.current.has(target.id) &&
          now >= target.end.getTime() - END_LEAD_MS &&
          now <= target.end.getTime() + END_GRACE_MS
        ) {
          notifiedEndRef.current.add(target.id)
          persistDayset('meeting-reminder-ended', notifiedEndRef.current)
          fireEndPrompt(target, () => endRecording())
        }
      }
    }

    tick()
    const id = window.setInterval(tick, TICK_MS)
    return () => window.clearInterval(id)
  }, [enabled])

  // ---- smart end detection (farewell words + sustained silence) -------------
  // The calendar prompt above only covers scheduled meetings near their end
  // time. This watches the recording itself: live-caption farewell phrases
  // confirmed by silence, or a long stretch with no audio at all (the classic
  // forgot-to-stop). One prompt per recording, whichever signal fires first.
  useEffect(() => {
    if (!detectEnabled || recordingState !== 'recording') return

    let lastLoudAt = Date.now()
    let farewellAt: number | null = null
    const recordingId = useRecordingStore.getState().recordingId
    if (smartEndScanRef.current.recordingId !== recordingId) {
      smartEndScanRef.current = { recordingId, count: 0 }
    }
    let scannedCaptions = smartEndScanRef.current.count

    const tick = () => {
      const store = useRecordingStore.getState()
      if (store.state !== 'recording') {
        // Idle/paused/finalizing: nothing is "silent", and a fresh session
        // starts its clocks over.
        lastLoudAt = Date.now()
        if (store.state === 'idle') {
          farewellAt = null
          scannedCaptions = 0
        }
        return
      }
      const now = Date.now()

      if (
        Math.max(store.levels.mic, store.levels.system) >
        SMART_END_SILENCE_LEVEL
      ) {
        lastLoudAt = now
      }

      // Scan only caption lines that arrived since the last tick
      const captions = store.captions
      if (captions.length > scannedCaptions) {
        for (let i = scannedCaptions; i < captions.length; i++) {
          if (containsFarewell(captions[i].text)) {
            farewellAt = now
          }
        }
        scannedCaptions = captions.length
        smartEndScanRef.current.count = scannedCaptions
      }
      // Conversation kept going after the goodbye — false alarm
      if (farewellAt && now - farewellAt > FAREWELL_EXPIRY_MS) {
        farewellAt = null
      }

      if (smartEndPromptedRef.current === store.recordingId) return
      if (store.durationMs < SMART_END_MIN_RECORDING_MS) return

      const silentForMs = now - lastLoudAt
      if (farewellAt && silentForMs >= FAREWELL_CONFIRM_SILENCE_MS) {
        smartEndPromptedRef.current = store.recordingId
        fireSmartEndPrompt(
          'Sounds like the meeting wrapped up',
          'Goodbyes were said and it went quiet — stop the recording?',
          () => endRecording(),
        )
      } else if (silentForMs >= LONG_SILENCE_MS) {
        smartEndPromptedRef.current = store.recordingId
        fireSmartEndPrompt(
          'Still recording?',
          `It's been quiet for ${Math.round(silentForMs / 60000)} minutes — stop the recording?`,
          () => endRecording(),
        )
      }
    }

    const id = window.setInterval(tick, SMART_END_TICK_MS)
    return () => window.clearInterval(id)
  }, [detectEnabled, recordingState])

  // ---- ad-hoc meeting detection (no calendar entry) --------------------------
  // A conferencing app is running AND something holds the mic open → offer to
  // record. Calendar-scheduled meetings are handled by the tick above; if one
  // is starting around now we stay quiet and let that prompt win.
  useEffect(() => {
    if (!detectEnabled || recordingState !== 'idle') return

    let disposed = false
    let inFlight = false
    const probe = async () => {
      const { state } = useRecordingStore.getState()
      if (state !== 'idle') return

      if (disposed || inFlight) return
      inFlight = true
      let detection
      try {
        detection = await detectActiveMeeting()
      } finally {
        inFlight = false
      }
      if (disposed || useRecordingStore.getState().state !== 'idle') return
      const inCall = Boolean(detection?.conferencingApp && detection.micInUse)

      if (!inCall) {
        detectSessionActiveRef.current = false
        return
      }
      // Same call session — already handled (prompted or ignored).
      if (detectSessionActiveRef.current) return
      detectSessionActiveRef.current = true

      if (Date.now() < detectSnoozedUntilRef.current) return
      detectSnoozedUntilRef.current = Date.now() + DETECT_SNOOZE_MS

      // A calendar meeting starting around now? Its own prompt covers this.
      const now = Date.now()
      const calendarCovers = meetingsRef.current.some(
        m => Math.abs(m.start.getTime() - now) <= START_ALIGN_TOLERANCE_MS,
      )
      if (calendarCovers) return

      // Deliberately generic copy: the detected app is just "a conferencing
      // app is running" — Teams auto-starts at login, and a Zoom call in the
      // browser matches the idle Teams process, so naming the app names the
      // wrong one. The signal is trustworthy; the attribution is not.
      logger.info(
        `Ad-hoc meeting detected (app hint: ${detection?.conferencingApp ?? 'unknown'})`,
      )
      const title = 'Meeting'
      void browserNotificationService.showLocalReminder({
        title: "Looks like you're in a meeting",
        body: 'Click to record it with Scrambl.',
        tag: `meeting-record-adhoc-${now}`,
        data: { type: 'meeting-record', title },
      })
      if (document.hasFocus()) {
        toast("Looks like you're in a meeting", {
          description: 'Record it with AI notes?',
          action: {
            label: 'Record',
            onClick: () => void beginRecording(title, undefined, { title }),
          },
          duration: 30_000,
        })
      }
    }

    void probe()
    const id = window.setInterval(() => void probe(), DETECT_TICK_MS)
    return () => {
      disposed = true
      window.clearInterval(id)
    }
  }, [detectEnabled, recordingState])

  return null
}

/**
 * Which meeting (if any) did the in-progress recording start in alignment with?
 * Only such recordings get the end-of-meeting stop prompt — an ad-hoc recording
 * that doesn't line up with a meeting is left alone.
 *
 * Prefer the meeting we explicitly started from a reminder; otherwise match a
 * meeting whose START is within tolerance of when the recording actually began.
 */
function pickEndCandidate(
  meetings: RecordableMeeting[],
  activeMeetingId: string | null,
  recordingStartMs: number | null,
): RecordableMeeting | undefined {
  if (activeMeetingId) {
    return meetings.find(m => m.id === activeMeetingId)
  }
  if (recordingStartMs == null) return undefined
  return meetings.find(
    m =>
      Math.abs(m.start.getTime() - recordingStartMs) <=
      START_ALIGN_TOLERANCE_MS,
  )
}

function fireStartPrompt(
  meeting: RecordableMeeting,
  onAccept: () => void,
): void {
  const body = 'Your meeting is starting. Click to record it with Scrambl.'
  void browserNotificationService.showLocalReminder({
    title: meeting.title,
    body,
    tag: `meeting-record-${meeting.id}`,
    data: { type: 'meeting-record', eventId: meeting.id, title: meeting.title },
  })
  // Desktop notification clicks are unreliable; if the user is at the app,
  // give them a reliable in-app action too.
  if (document.hasFocus()) {
    toast(`${meeting.title} is starting`, {
      description: 'Record this meeting?',
      action: { label: 'Record', onClick: onAccept },
      duration: 30_000,
    })
  }
}

/** Smart end prompt (farewell/silence detected). The OS notification reuses
 *  the `meeting-record-stop` click bridge, so clicking it stops immediately. */
function fireSmartEndPrompt(
  title: string,
  description: string,
  onStop: () => void,
): void {
  void browserNotificationService.showLocalReminder({
    title,
    body: `${description} Click to stop now.`,
    tag: `meeting-record-stop-smart-${Date.now()}`,
    data: { type: 'meeting-record-stop', title },
  })
  if (document.hasFocus()) {
    toast(title, {
      description,
      action: { label: 'Stop & generate notes', onClick: onStop },
      cancel: { label: 'Keep recording', onClick: () => {} },
      duration: 60_000,
    })
  }
}

function fireEndPrompt(meeting: RecordableMeeting, onStop: () => void): void {
  void browserNotificationService.showLocalReminder({
    title: `${meeting.title} is ending`,
    body: 'Stop recording when you’re done — click to stop now.',
    tag: `meeting-record-stop-${meeting.id}`,
    data: {
      type: 'meeting-record-stop',
      eventId: meeting.id,
      title: meeting.title,
    },
  })
  if (document.hasFocus()) {
    toast(`${meeting.title} is ending`, {
      description: 'Stop the recording?',
      action: { label: 'Stop', onClick: onStop },
      duration: 30_000,
    })
  }
}
